import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UserMaintenanceService } from 'src/modules/api/user-maintenance.service';

import { forkJoin, iif, of, Subject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { debounceTime, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CgpAlertDialogService } from 'src/app/shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { ToastrService } from 'ngx-toastr';

@Component({
   templateUrl: './add-user-modal.template.html',
   styleUrls: ['./add-user-modal.scss']
})
export class AddUserModalComponent implements OnInit, OnDestroy {

   private readonly unsubscribe$ = new Subject<void>();
   public readonly GMIN_LENGTH = 9;

   _isRequestor = false;
   loading = true;
   searchingUser = false;
   selectedRequestor;

   roles;
   constSources;
   designSources;

   addUserForm: UntypedFormGroup;
   foundUser: { firstName: string, lastName: string, email: string };
   denyUser: { gmin: string, reason: string };
   denyReason: string;

   constructor(
      public dialogRef: MatDialogRef<AddUserModalComponent>,
      private readonly toastr: ToastrService,
      private readonly fb: UntypedFormBuilder,
      private readonly userMaintenanceService: UserMaintenanceService,
      private readonly cgpAlertDialogService: CgpAlertDialogService,
      @Inject(MAT_DIALOG_DATA) public readonly data,
   ) {
      this.addUserForm = this.fb.group({
         gmin: [''],
         userRoleId: [],
         userSourceId: [],
         restrictedToId: [],
         active: [true]
      }, { validator: this.securityLevelConstSourceValidator });
   }

   /**
    * Flag to determine if the user being added is not a new user
    * but a requestor. 
    *
    * @readonly
    * @type {boolean}
    * @memberof AddUserModalComponent
    */
   get isRequestor(): boolean {
      return this.data && this.data.isRequestor;
   }

   get requestType(): 'grant' | 'deny' {
      return this.data && this.data.requestType;
   }

   get dialogTitle(): string {
      let dialogTitle = '';
      if (this.isRequestor && this.selectedRequestor) {
         dialogTitle = `${(this.requestType === 'grant') ? 'Grant' : 'Deny'} ${this.selectedRequestor.firstName} ${this.selectedRequestor.lastName} Access`;
      } else {
         dialogTitle = 'Add User';
      }
      return dialogTitle;
   }

   get validFoundUser(): boolean {
      return !!this.foundUser;
   }

   ngOnInit() {
      this.loadLists();
      this.setupUserInputSearch();

      if (this.data && this.data.selectedRequestor) {
         this.selectedRequestor = {
            gmin: this.data.selectedRequestor.rGmin,
            firstName: this.data.selectedRequestor.rFirstName,
            lastName: this.data.selectedRequestor.rLastName
         };
      }
   }

   ngOnDestroy(): void {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
   }

   loadLists() {
      this.loading = true;

      forkJoin(
         this.userMaintenanceService.getRoles(),
         this.userMaintenanceService.getConstSources(),
         this.userMaintenanceService.getDesignSources()
      ).pipe(finalize(() => this.loading = false))
         .subscribe(([roles, constSources, designSources]) => {
            this.roles = roles;
            this.constSources = constSources;
            this.designSources = designSources;
            // Set the default values for the corresponding dropdowns
            this.addUserForm.controls.userRoleId.setValue(this.collectionValue('Other', 'userRole', this.roles));
            this.addUserForm.controls.restrictedToId.setValue(this.collectionValue('ALL SOURCES', 'sourceName', this.constSources));
         });
   }

   setupUserInputSearch() {
      this.addUserForm.controls.gmin.valueChanges
         .pipe(
            takeUntil(this.unsubscribe$),
            tap(val => {
               if (val && val.length !== this.GMIN_LENGTH) {
                  this.foundUser = null;
               } else {
                  this.searchingUser = true;
               }
            }),
            debounceTime(600),
            switchMap(val => iif(() => val && val.length === this.GMIN_LENGTH,
               this.userMaintenanceService.findUserByGmin(val)
                  .pipe(finalize(() => this.searchingUser = false)),
               of(null))),
         )
         .subscribe(res => {
            if (res) {
               this.foundUser = {
                  firstName: res.givenName,
                  lastName: res.sn,
                  email: res.email
               };
            } else {
               this.searchingUser = false;
            }
         });
   }

   securityLevelConstSourceValidator(group: UntypedFormGroup): ValidationErrors | null {
      const roleId = group.get('userRoleId').value;
      const constId = group.get('restrictedToId').value;
      return roleId > 4 || constId === 127 ? null : { 'securityLevelConstSource': { value: roleId } };
   }

   onAddUserClick() {
      this.loading = true;
      const body = this.addUserForm.value;

      this.userMaintenanceService.createUser(body)
         .pipe(finalize(() => this.loading = false))
         .subscribe({
            next: () => {
               this.toastr.success('User added to TPRECM successfully.', 'Add User Complete');
               this.dialogRef.close();
            }
         });
   }

   onGrantUserClick() {
      this.loading = true;
      const user = this.addUserForm.value;
      user['firstName'] = this.selectedRequestor.firstName || '';
      user['lastName'] = this.selectedRequestor.lastName || '';
      user['gmin'] = this.selectedRequestor.gmin;
      this.userMaintenanceService.grantUserAccess(user)
         .pipe(finalize(() => this.loading = false))
         .subscribe({
            next: () => {
               this.toastr.success(`${user.firstName} ${user.lastName} has been added to TPRECM successfully`, 'Grant Access Complete');
               this.dialogRef.close({ reload: true });
            }
         });
   }

   onDenyUserClick() {

      if (this.denyReason.trim().length === 0) {
         this.toastr.error('Deny reason cannot be empty!');
         this.denyReason = '';
         return;

      }
      this.loading = true;
      this.denyUser = {
         gmin: this.selectedRequestor.gmin,
         reason: this.denyReason.trim()
      };

      this.userMaintenanceService.denyUserAccess(this.denyUser)
         .pipe(finalize(() => this.loading = false))
         .subscribe({
            next: () => {
               this.toastr.error(`${this.selectedRequestor.firstName} ${this.selectedRequestor.lastName} denied access to TPRECM.`, 'Deny Access Complete');
               this.dialogRef.close({ reload: true });
            }
         });

   }

   collectionValue(val, fieldName, collection) {
      let result = '';
      if (collection) {
         result = collection.find(el => el[fieldName] === val).id;
      }
      return result;
   }
}
